import React, { useContext, useRef, useState } from "react";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";
import FileUploader from "../../common/FileUploader";
import TextBox from "../../common/TextBox";
import { Form, Formik } from "formik";
import { ReactComponent as AddCustomSvg } from "../../../assets/newProject/add-custom.svg";
import { ReactComponent as InfoSvg } from "../../../assets/icons/info.svg";
import returnIconImage from "../../../assets/panorama/return.png";
import { Context } from "./index";
import * as Tooltip from "react-tooltip";

// * Hotspot icons
import hotspotPinImage from "../../../assets/panorama/hotspot.gif";
import hotspotPin2Image from "../../../assets/panorama/icons/hotspot-3.gif";
import hotspotPin3Image from "../../../assets/panorama/icons/hotspot-4.gif";
import imagePinImage from "../../../assets/panorama/icons/image.gif";
import leftTurnImage from "../../../assets/panorama/icons/left.gif";
import rightTurnImage from "../../../assets/panorama/icons/right.gif";
import forwardTurnImage from "../../../assets/panorama/icons/forward.gif";
import leftTurn2Image from "../../../assets/panorama/icons/left-2.gif";
import rightTurn2Image from "../../../assets/panorama/icons/right-2.gif";
import voiceMemoImage from "../../../assets/panorama/icons/voice.gif";
import _ from "lodash";

const HOTSPOT_TYPE = {
  WALK_WAY: 1,
  INFO: 2,
  VOICE_MEMO: 3,
  PICTURE: 4,
};

const AddHotspotWizard = () => {
  const { getLang } = useContext(AppContext);
  const TranslationCommon = getLang(langComponent.common);
  const {
    images,
    cycles,
    materialGroups,
    selectedImageName: selectedImageName,
    latestPitchYaw,
    scenes,
    setScenes,
    closeOverlay,
    switchToPlaceHotspot,
    panoramaCovers,
  } = useContext(Context);

  const [step, setStep] = useState("");
  const [type, setType] = useState("");
  const [infoTitle, setInfoTitle] = useState("");
  const [infoDesc, setInfoDesc] = useState("");
  const [voiceFile, setVoiceFile] = useState(undefined);
  const [imageFile, setImageFile] = useState(undefined);
  const [customIcon, setCustomIcon] = useState(undefined);
  const [selectedIconIndex, setSelectedIconIndex] = useState(1);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(undefined);
  const [sceneName, setSceneName] = useState("");
  const formikRef = useRef(null);

  // TODO: Disable hotspot if no more images to swap
  // const isDisableAddHotspot = scenes.find((s, i) => i === selectedImageIndex);

  const navigateToSelectScene = () => {
    setType("swap");
    setStep("swap");
  };

  // * Change step
  const navigateToAddInfo = () => {
    setType("info");
    setStep("info");
  };

  const navigateToAddVoiceMemo = () => {
    setType("voice");
    setStep("voice");
  };

  const navigateToSelectHotspotType = () => {
    setType("");
    setStep("");
    setInfoTitle("");
    setInfoDesc("");
    setVoiceFile(undefined);
    setImageFile(undefined);
  };

  const navigateToSceneConfirmation = (imageSelectedindex) => {
    setSelectedSceneIndex(imageSelectedindex);
    setSceneName(scenes[imageSelectedindex]?.name ?? "");
    setStep("scene-confirm");
  };

  const navigateToAddPicture = () => {
    setType("picture");
    setStep("picture");
  };

  // * Event handlers
  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "title") {
      setInfoTitle(value);
      return;
    }

    setInfoDesc(value);
  };

  const handleVoiceFileChanged = (files) => {
    setVoiceFile(files[0]);

    formikRef.current?.setFieldValue("voice", files[0]);
  };

  const handlePictureFileChanged = (files) => {
    setImageFile(files[0]);

    formikRef.current?.setFieldValue("picture", files[0]);
  };

  const handleNavigateToPlaceHotpsot = () => {
    if (type === "info") {
      if (!infoTitle || !infoDesc) {
        formikRef.current?.submitForm();
        return;
      }

      setStep("hotspot");
      switchToPlaceHotspot({
        type: HOTSPOT_TYPE.INFO,
        title: infoTitle,
        desc: infoDesc,
        icon: 4,
      });
    }

    if (type === "voice") {
      if (!infoTitle || !infoDesc || !voiceFile) {
        formikRef.current?.submitForm();
        return;
      }

      setStep("hotspot");

      switchToPlaceHotspot({
        type: HOTSPOT_TYPE.VOICE_MEMO,
        title: infoTitle,
        desc: infoDesc,
        icon: 4,
        file: voiceFile,
      });
      setVoiceFile(undefined);
    }

    if (type === "swap") {
      if (!sceneName) {
        formikRef.current?.submitForm();
        return;
      }

      setStep("hotspot");
      switchToPlaceHotspot({
        type: HOTSPOT_TYPE.WALK_WAY,
        title: sceneName,
        icon: selectedIconIndex,
        customIcon,
        sceneIndex: selectedSceneIndex,
        sceneName: images[selectedSceneIndex].name,
      });
    }

    if (type === "picture") {
      if (!infoTitle || !imageFile) {
        formikRef.current?.submitForm();
        return;
      }

      setStep("hotspot");

      switchToPlaceHotspot({
        type: HOTSPOT_TYPE.PICTURE,
        title: infoTitle,
        icon: 4,
        file: imageFile,
      });
      setImageFile(undefined);
    }
  };

  const HotspotIconDiv = ({ index, icon, name, isCustom, onSelect }) => {
    const [file, setFile] = useState(undefined);

    const handleSelectHotspotIcon = (file) => {
      onSelect({
        index: isCustom ? -1 : index,
        icon: isCustom ? file : null,
      });
    };

    const handleFileChanged = (e) => {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      try {
        setFile(e.target.files[0]);
        handleSelectHotspotIcon(e.target.files[0]);
      } catch (error) {
        console.error(error);
      }
    };

    const classNames = [
      `new-project-new-hotspot-content-arrows-item`,
      `new-project-new-hotspot-content-arrows-item--${
        isCustom ? "custom" : index
      }`,
    ];

    if (selectedIconIndex === index) {
      classNames.push("new-project-new-hotspot-content-arrows-item--active");
    }

    return (
      <div
        className={classNames.join(" ")}
        onClick={() => {
          if (isCustom) return;
          handleSelectHotspotIcon();
        }}
      >
        {!isCustom && (
          <img
            className="new-project-new-hotspot-content-arrows-item__icon"
            alt="arrow"
            src={icon}
          />
        )}
        {isCustom && (
          <div className="new-project-new-hotspot-content-arrows-item-custom">
            {!file && <AddCustomSvg width={28} />}
            <input
              id="hotpsot-custom-icon"
              className="fileuploader-original"
              type="file"
              onChange={handleFileChanged}
            />
            <label
              className="new-project-new-hotspot-content-arrows-item-custom-label"
              htmlFor="hotpsot-custom-icon"
            >
              {!file && "Custom"}
              {file && (
                <img
                  className="new-project-new-hotspot-content-arrows-item-custom--image"
                  alt="custom-icon"
                  src={URL.createObjectURL(file)}
                  htmlFor="hotpsot-custom-icon"
                />
              )}
            </label>
          </div>
        )}
      </div>
    );
  };

  const handleSelectIconClick = ({ index, icon }) => {
    setCustomIcon(icon);
    setSelectedIconIndex(index);
  };

  const getHeaderRender = () => {
    if (!step || step === "swap") {
      return (
        <div
          className="new-project-new-hotspot-return"
          onClick={() => closeOverlay()}
        >
          <img src={returnIconImage} height={17} />
          <span className="new-project-new-hotspot-return__text">
            {TranslationCommon("Cancel")}
          </span>
        </div>
      );
    }

    return (
      <>
        <div
          className="new-project-new-hotspot-return"
          onClick={navigateToSelectHotspotType}
        >
          <img src={returnIconImage} height={17} />
          <span className="new-project-new-hotspot-return__text">
            {TranslationCommon("Return")}
          </span>
        </div>
        <div
          className="new-project-new-hotspot-final__confirm"
          onClick={handleNavigateToPlaceHotpsot}
        >
          Continue
        </div>
      </>
    );
  };

  const getContentRender = () => {
    if (step === "info") {
      return (
        <Formik
          innerRef={formikRef}
          initialValues={{ title: infoTitle, desc: infoDesc }}
          validate={({ title, desc }) => {
            let errors = {};

            if (!title) errors.title = "Please specify a title";
            if (!desc) errors.desc = "Required";

            if (!_.isEmpty(errors)) {
              return errors;
            }

            setInfoTitle(title);
            setInfoDesc(desc);
          }}
        >
          <Form className="new-project-new-hotspot-content-scenes">
            <TextBox name="title" label={TranslationCommon("Title")} />
            <TextBox name="desc" label={TranslationCommon("Description")} />
          </Form>
        </Formik>
      );
    }

    if (step === "voice") {
      return (
        <Formik
          innerRef={formikRef}
          initialValues={{ title: infoTitle, desc: infoDesc, voice: undefined }}
          validate={({ title, desc, voice }) => {
            if (!title || !desc || !voice) {
              let error = {};
              if (!title) error.title = "Specify title";
              if (!desc) error.desc = "Specify description";
              if (!voice) error.voice = "Upload a audio file";
              return error;
            }

            setInfoTitle(title);
            setInfoDesc(desc);
            return {};
          }}
        >
          {({ touched, errors }) => {
            return (
              <Form className="new-project-new-hotspot-content-scenes">
                <TextBox name="title" label={TranslationCommon("Title")} />
                <TextBox name="desc" label={TranslationCommon("Description")} />
                <div className="new-project-new-hotspot-content-scenes__title">
                  {TranslationCommon("Voice memo")}
                </div>
                <FileUploader
                  id={"voice"}
                  accept={"audio/mp3,audio/*;capture=microphone"}
                  label={voiceFile ? "Change audio" : "Select audio"}
                  onChange={handleVoiceFileChanged}
                />
                {voiceFile && (
                  <span className="new-project-new-hotspot-content-scenes__audio-file">
                    {voiceFile?.name}
                  </span>
                )}
                {touched.voice && errors.voice && (
                  <span
                    style={{ color: "red", textAlign: "center", fontSize: 12 }}
                  >
                    {errors.voice}
                  </span>
                )}
              </Form>
            );
          }}
        </Formik>
      );
    }

    if (step === "picture") {
      return (
        <Formik
          innerRef={formikRef}
          initialValues={{ title: infoTitle, picture: undefined }}
          validate={({ title, picture }) => {
            let errors = {};
            if (!title) errors.title = "Specify title";
            if (!picture) errors.picture = "Upload a picture";

            if (!_.isEmpty(errors)) {
              return errors;
            }

            setInfoTitle(title);
            return {};
          }}
        >
          {({ touched, errors }) => {
            return (
              <Form className="new-project-new-hotspot-content-scenes">
                <TextBox name="title" label={TranslationCommon("Title")} />
                <div className="new-project-new-hotspot-content-scenes__title">
                  Image
                </div>
                {imageFile && (
                  <img
                    className="new-project-new-hotspot-content-scenes__image"
                    alt=""
                    src={URL.createObjectURL(imageFile)}
                  />
                )}
                <FileUploader
                  id={"picture"}
                  accept={"image"}
                  label={"Select file"}
                  onChange={handlePictureFileChanged}
                />
                {touched.picture && errors.picture && (
                  <span
                    style={{ color: "red", textAlign: "center", fontSize: 12 }}
                  >
                    {errors.picture}
                  </span>
                )}
              </Form>
            );
          }}
        </Formik>
      );
    }

    if (step === "swap") {
      return (
        <div className="new-project-new-hotspot-content-scenes">
          <div className="new-project-new-hotspot-content-scenes__title">
            {TranslationCommon("Select scene")}
          </div>
          <div className="new-project-new-hotspot-content-scenes-options">
            {images.map((img, i) => {
              if (img.name === selectedImageName) return <></>;

              function checkIsUsed() {
                // * Don't include scenes that are already linked in materials or cycle
                // * Hotspot filtering
                const _hotspotFound =
                  scenes.find((s) => s.imageName === selectedImageName)
                    ?.hotspots ?? [];

                const _foundLink = _hotspotFound.find(
                  (h) => h.sceneName === img.name
                );

                if (!!_foundLink) {
                  return true;
                }

                // * Cycle filtering
                const _cycle = cycles.find(
                  (c) => c.day === img.name || c.night === img.name
                );

                if (_cycle) {
                  if (
                    _cycle.day === selectedImageName &&
                    _cycle.night === img.name
                  )
                    return true;

                  if (
                    _cycle.day === img.name &&
                    _cycle.night === selectedImageName
                  )
                    return true;
                }

                // * Material filtering
                const _materialGroup = materialGroups.find((group) =>
                  group.find((m) => m.to === img.name)
                );

                if (
                  _materialGroup &&
                  _materialGroup.find((m) => m.to === images[i].name)
                ) {
                  return true;
                }

                return false;
              }

              const isUsed = checkIsUsed();

              return (
                <img
                  className={`new-project-new-hotspot-content-scenes-options__option ${
                    isUsed
                      ? "new-project-new-hotspot-content-scenes-options__option--disabled"
                      : ""
                  }`}
                  src={URL.createObjectURL(img)}
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={
                    isUsed
                      ? "Already linked as a swap scene, material or day/night cycle"
                      : ""
                  }
                  onClick={() => {
                    if (isUsed) {
                      return;
                    }
                    navigateToSceneConfirmation(i);
                  }}
                />
              );
            })}
          </div>
        </div>
      );
    }

    if (step === "scene-confirm") {
      return (
        <>
          <img
            className="new-project-new-hotspot-content-final__img"
            src={URL.createObjectURL(images[selectedSceneIndex])}
            alt="panomara"
          />
          <Formik
            innerRef={formikRef}
            initialValues={{ name: scenes[selectedSceneIndex].name }}
            validate={({ name }) => {
              if (!name) return { name: "Please enter a name" };

              // * Check if scene name already exists except the current one
              const _duplicateSceneName = scenes.find((s, index) => {
                if (index === selectedSceneIndex) return false;

                return s.name === name;
              });

              if (_duplicateSceneName)
                return { name: "Scene with same name exist already" };

              setSceneName(name);
            }}
          >
            <Form style={{ margin: "0 10px" }}>
              <TextBox
                name="name"
                label="Scene name"
                placeholder="Name the scene"
              />
            </Form>
          </Formik>
          <h4 className="new-project-new-hotspot-content__heading4">
            {TranslationCommon("Select icon")}
          </h4>
          <div className="new-project-new-hotspot-content-arrows">
            <HotspotIconDiv
              index={1}
              icon={forwardTurnImage}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={2}
              icon={leftTurnImage}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={3}
              icon={leftTurn2Image}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={4}
              icon={rightTurnImage}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={5}
              icon={rightTurn2Image}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={6}
              icon={hotspotPinImage}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={7}
              icon={hotspotPin2Image}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv
              index={8}
              icon={hotspotPin3Image}
              onSelect={handleSelectIconClick}
            />
            <HotspotIconDiv isCustom={true} onSelect={handleSelectIconClick} />
          </div>
        </>
      );
    }

    return (
      <div className="new-project-new-hotspot-content-select-type">
        <span
          className={`new-project-new-hotspot-content-select-type__option ${
            images.length === 1
              ? "new-project-new-hotspot-content-select-type__option--disable"
              : ""
          }`}
          onClick={() => {
            if (images.length === 1) {
              alert("Not enough panorama");
              return;
            }

            navigateToSelectScene();
          }}
        >
          {TranslationCommon("Swap scene")}
          <div
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Make a tour by swaping to another panorama"
            style={{ width: "fit-content", height: "fit-content" }}
          >
            <InfoSvg
              width={16}
              style={{ opacity: 0.6, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
        </span>
        <span
          className="new-project-new-hotspot-content-select-type__option"
          onClick={navigateToAddInfo}
        >
          {TranslationCommon("Add info")}
        </span>
        <span
          className="new-project-new-hotspot-content-select-type__option"
          onClick={navigateToAddVoiceMemo}
        >
          {TranslationCommon("Add voice memo")}
        </span>
        <span
          className="new-project-new-hotspot-content-select-type__option"
          onClick={navigateToAddPicture}
        >
          Add picture
        </span>
      </div>
    );
  };

  if (step === "hotspot") {
    return <></>;
  }

  return (
    <div className="new-project-new-hotspot">
      {step !== "hotspot" && (
        <div className="new-project-new-hotspot-content">
          <div className="new-project-new-hotspot-content-header">
            {getHeaderRender()}
          </div>
          {getContentRender()}
        </div>
      )}
      <Tooltip.Tooltip id="my-tooltip" />
    </div>
  );
};

export default AddHotspotWizard;
